// SPEC-54 · the words of « + Nova utilizadora »: the base's button, the
// side panel, its checks and its outcome. PT first (BRAND.md), FR and EN
// key for key. The laterality question and answers are src/i18n/laterality*.ts.

import type { Words } from "../shell";

/**
 * The modality field's suggestions. Values, stored as typed and matched by
 * poweryourflow-lib's laterality table in PT, so PT in every language, as
 * Dados gerais' level chips are.
 */
export const MODALITY_SUGGESTIONS = ["Futebol", "Atletismo", "Boxe", "Andebol", "Basquetebol", "Voleibol", "Rugby", "Natação", "Ginástica"] as const;

export const newAccountPt = {
  /** The base's primary button, after a « + », and the panel's kicker. */
  open: "Nova utilizadora",
  title: "Criar conta à mão.",
  lead: "Para atletas vistas em consulta sem app. A atleta recebe o convite por email e completa o resto na app.",
  close: "Fechar",
  identity: "Identidade",
  sport: "Desporto",
  name: "Nome",
  namePlaceholder: "Mariana Alves",
  email: "Email",
  emailPlaceholder: "nome@email.pt",
  birthDate: "Data de nascimento",
  birthDatePlaceholder: "dd/mm/aaaa",
  /** Her app's language and her invitation's. */
  language: "Idioma",
  modality: "Modalidade",
  position: "Posição",
  positionPlaceholder: "Ex.: médio, 400 m, −60 kg",
  club: "Clube",
  /** An empty club is stored as « Sem clube ». */
  clubPlaceholder: "Sem clube",
  level: "Nível",
  note: "A conta fica «Ativa» sem avaliações. Os dados gerais ficam preenchidos; a atleta confirma-os na primeira sessão.",
  submit: "Criar conta e enviar convite",
  submitting: "A criar a conta…",
  /** In the card's order: what is missing, the email, the birth date, then the server's answer. */
  errors: {
    required: "Nome, email e data de nascimento são obrigatórios.",
    email: "Email inválido.",
    birthDate: "Data de nascimento inválida. Escreve assim: 14/03/2001.",
    taken: "Já existe uma conta com este email.",
    failed: "Não consegui criar a conta. Tenta outra vez.",
  },
  /** On her profile, once, when the account exists but the email could not go: the app's own link, in the admin's words. */
  notInvited: (forgot: string) => `Conta criada. O convite não foi enviado: pede-lhe que use «${forgot}» na app.`,
} as const;

export const newAccountFr: Words<typeof newAccountPt> = {
  open: "Nouvelle utilisatrice",
  title: "Créer un compte à la main.",
  lead: "Pour les athlètes vues en consultation sans l’app. L’athlète reçoit l’invitation par email et complète le reste dans l’app.",
  close: "Fermer",
  identity: "Identité",
  sport: "Sport",
  name: "Nom",
  namePlaceholder: "Mariana Alves",
  email: "Email",
  emailPlaceholder: "nom@email.fr",
  birthDate: "Date de naissance",
  birthDatePlaceholder: "jj/mm/aaaa",
  language: "Langue",
  modality: "Discipline",
  position: "Poste",
  positionPlaceholder: "Ex. : milieu, 400 m, −60 kg",
  club: "Club",
  clubPlaceholder: "Sans club",
  level: "Niveau",
  note: "Le compte est « Active » sans évaluation. Les données générales sont remplies ; l’athlète les confirme à sa première session.",
  submit: "Créer le compte et envoyer l’invitation",
  submitting: "Création du compte…",
  errors: {
    required: "Nom, email et date de naissance sont obligatoires.",
    email: "Email invalide.",
    birthDate: "Date de naissance invalide. Écris-la ainsi : 14/03/2001.",
    taken: "Un compte existe déjà avec cet email.",
    failed: "Je n’ai pas pu créer le compte. Réessaie.",
  },
  notInvited: (forgot: string) => `Compte créé. L’invitation n’a pas été envoyée : demande-lui d’utiliser « ${forgot} » dans l’app.`,
};

export const newAccountEn: Words<typeof newAccountPt> = {
  open: "New user",
  title: "Create an account by hand.",
  lead: "For athletes seen in consultation without the app. The athlete gets the invitation by email and fills in the rest in the app.",
  close: "Close",
  identity: "Identity",
  sport: "Sport",
  name: "Name",
  namePlaceholder: "Mariana Alves",
  email: "Email",
  emailPlaceholder: "name@email.com",
  birthDate: "Date of birth",
  birthDatePlaceholder: "dd/mm/yyyy",
  language: "Language",
  modality: "Discipline",
  position: "Position",
  positionPlaceholder: "E.g. midfielder, 400 m, −60 kg",
  club: "Club",
  clubPlaceholder: "No club",
  level: "Level",
  note: "The account is “Active” with no assessments. Her general info is filled in; she confirms it at her first session.",
  submit: "Create account and send invitation",
  submitting: "Creating the account…",
  errors: {
    required: "Name, email and date of birth are required.",
    email: "Invalid email.",
    birthDate: "Invalid date of birth. Write it like this: 14/03/2001.",
    taken: "An account with this email already exists.",
    failed: "I couldn’t create the account. Try again.",
  },
  notInvited: (forgot: string) => `Account created. The invitation was not sent: ask her to use “${forgot}” in the app.`,
};
